import React from 'react';
import { Dialog, Heading, type DialogProps } from '@innovationnorway/cl-react';
import { useTranslation } from 'react-i18next';

export type DialogContainerProps = {
  children: React.ReactNode;
  show: boolean;
  onClose?: () => void;
  closedBy?: DialogProps['closedby'];
  title: string;
  showCloseButton?: boolean;
  closeButtonLabel?: string;
  className?: string;
  titleHeadingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  initialFocusRef?: React.RefObject<HTMLElement>;
};

const DialogContainer: React.FC<DialogContainerProps> = ({
  show,
  title,
  children,
  onClose,
  closedBy = 'any',
  showCloseButton = true,
  closeButtonLabel,
  className,
  titleHeadingLevel = 3,
  initialFocusRef,
}) => {
  const { t } = useTranslation();
  const titleId = React.useId();
  const resolvedCloseButtonLabel = closeButtonLabel ?? t('closeDialog');
  const dialogRef = React.useRef<HTMLDialogElement>(null);

  React.useEffect(() => {
    if (show && dialogRef.current?.open) {
      initialFocusRef?.current?.focus();
    }
  }, [show, initialFocusRef]);

  return (
    <Dialog
      ref={dialogRef}
      open={show}
      onClose={onClose}
      closeButton={showCloseButton ? resolvedCloseButtonLabel : undefined}
      className={className}
      closedby={closedBy}
      aria-labelledby={titleId}
    >
      <Heading id={titleId} level={titleHeadingLevel} data-size="sm">
        {title}
      </Heading>
      {children}
    </Dialog>
  );
};

export default DialogContainer;
